<template>
  <er-card v-loading="loading">
    <layout-toolbar slot="header" justify="space-between">
      <p class="card-title">{{ $t("PM_individual") }}</p>
      <el-col :span="10" :md="10" :lg="9" :xl="8">
        <er-date-picker
          v-model="dateRange"
          :disableDateMethod="disableDateMethod"
          @change="handleValueChange"
        ></er-date-picker>
      </el-col>
    </layout-toolbar>
    <el-row :key="$i18n.locale">
      <!-- highcharts tag -->
      <high-charts
        :options="chartOptions"
        constructor-type="stockChart"
      ></high-charts>
    </el-row>
  </er-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import errorHandlerMixin from "@/mixins/errorHandlerMixin";
import chartMixin from "@/mixins/chartMixin";
import { pmScheduleGraph } from "./chartOptions";

export default {
  mixins: [errorHandlerMixin, chartMixin],
  data: function() {
    return {
      dateRange: [],
      feed_schedule_logs: [],
      params: {
        from_date: "",
        to_date: "",
        month_of: null,
        week_of: null
      },
      chartOptions: pmScheduleGraph,
      loading: false
    };
  },
  computed: {
    ...mapGetters("pondDetails", {
      getPmSlotWiseFeedGraph: "getPmSlotWiseFeedGraph",
      getPmIdPmDetailsOfSelectedPondId: "getPmIdPmDetailsOfSelectedPondId"
    }),
    defaultMomentDtRangeForCurrCulture() {
      return this.getMomentObjArrOfCultureDateRange;
    },
    pondMothersWithDetails() {
      return (this.getSelectedPond && this.getSelectedPond.pond_mothers) || [];
    },
    pondMothers() {
      if (this.pondMothersWithDetails) {
        const data = [];
        this.pondMothersWithDetails.forEach(ele => {
          data.push({ code: ele.code, id: ele._id });
        });
        return data;
      }
      return [];
    }
  },

  methods: {
    ...mapActions("pondDetails", {
      fetchSlotWiseFeed: "fetchSlotWiseFeed"
    }),
    async initComponent(pondId) {
      this.loading = true;
      // const feedLimit = {};
      // const dispensedLimit = {};
      try {
        await this.fetchSlotWiseFeed({
          deviceType: "pond_mother",
          params: this.params
        });
        this.feed_schedule_logs = this.getPmSlotWiseFeedGraph;
        this.initChart();
        this.initAxisTextKeys("Comn_date_and_time", "PM_dispensed_feed_kg");
        this.initChartLang();
      } catch (err) {
        this.errorMessages(err, true);
      } finally {
        this.loading = false;
      }
    },
    getPMScheduleFeedData(pondFeedDaysWise) {
      // data necessary
      // total feed for all time slots
      const pmsTotalTimeSlotDFeed = {};
      const pmsTotalDatesDataPresent = {};
      const mapCodeWithPmId = {};
      // const pmsScheduleDFeed = {};
      for (let iday = 0; iday < pondFeedDaysWise.length; iday++) {
        const day = pondFeedDaysWise[iday];
        const dateEpoch = this.$moment(day.date).valueOf();
        pmsTotalDatesDataPresent[dateEpoch] = iday;
      }
      const dateRange = [this.params.from_date, this.params.to_date];
      const fromDateInTimestamp = new Date(dateRange[0]).getTime();
      const toDateInTimestamp = new Date(dateRange[1]).getTime();

      const datesGenerated = this.createDatesBetweenToDates(
        fromDateInTimestamp,
        toDateInTimestamp
      );

      for (let iday = 0; iday < datesGenerated.length; iday++) {
        const checkingTimestamp = datesGenerated[iday];
        if (iday === 0) {
          for (let iPM = 0; iPM < this.pondMothers.length; iPM++) {
            const checkingPondMother = this.pondMothers[iPM];
            pmsTotalTimeSlotDFeed[checkingPondMother.code] = {};
            mapCodeWithPmId[checkingPondMother.code] = checkingPondMother.id;
          }
        }

        if (pmsTotalDatesDataPresent[checkingTimestamp] === undefined) {
          for (let iPM = 0; iPM < this.pondMothers.length; iPM++) {
            const checkingPondMother = this.pondMothers[iPM].code;
            pmsTotalTimeSlotDFeed[checkingPondMother][checkingTimestamp] = {};
            pmsTotalTimeSlotDFeed[checkingPondMother][
              checkingTimestamp
            ].tdfeed = 0;
            pmsTotalTimeSlotDFeed[checkingPondMother][
              checkingTimestamp
            ].tfeed = 0;
          }
          continue;
        }
        const day =
          pondFeedDaysWise[pmsTotalDatesDataPresent[checkingTimestamp]];
        const dateEpoch = this.$moment(day.date).valueOf();
        for (
          let iTimeSlot = 0;
          iTimeSlot < day.time_slots.length;
          iTimeSlot++
        ) {
          const timeSlot = day.time_slots[iTimeSlot];
          const strSTimeETime = timeSlot.s_time + " - " + timeSlot.e_time;
          for (let iPM = 0; iPM < timeSlot.pond_mothers.length; iPM++) {
            const pm = timeSlot.pond_mothers[iPM];
            // if the pm exists and the day exists in the pmsTotalTimeslotDFeed

            if (
              typeof pmsTotalTimeSlotDFeed[pm.pond_mother_code] !==
                "undefined" &&
              typeof pmsTotalTimeSlotDFeed[pm.pond_mother_code][
                dateEpoch.toString()
              ] !== "undefined"
            ) {
              pmsTotalTimeSlotDFeed[pm.pond_mother_code][dateEpoch].tdfeed +=
                pm.dispensed_feed;
              pmsTotalTimeSlotDFeed[pm.pond_mother_code][dateEpoch].tfeed +=
                pm.feed;
              pmsTotalTimeSlotDFeed[pm.pond_mother_code][dateEpoch][
                strSTimeETime
              ] = {
                timeslot: strSTimeETime,
                dfeed: pm.dispensed_feed,
                feed: pm.feed
              };
            } else {
              // found a new pond mother
              if (
                typeof pmsTotalTimeSlotDFeed[pm.pond_mother_code] ===
                "undefined"
              ) {
                pmsTotalTimeSlotDFeed[pm.pond_mother_code] = {};
              }
              // found a new date
              pmsTotalTimeSlotDFeed[pm.pond_mother_code][dateEpoch] = {};
              pmsTotalTimeSlotDFeed[pm.pond_mother_code][dateEpoch].tdfeed =
                pm.dispensed_feed;
              pmsTotalTimeSlotDFeed[pm.pond_mother_code][dateEpoch].tfeed =
                pm.feed;
              pmsTotalTimeSlotDFeed[pm.pond_mother_code][dateEpoch][
                strSTimeETime
              ] = {
                timeslot: strSTimeETime,
                dfeed: pm.dispensed_feed,
                feed: pm.feed
              };
            }
          }
        }
      }
      let series = [];
      const arrColors = this.getNColors(
        Object.keys(pmsTotalTimeSlotDFeed).length
      );
      Object.keys(pmsTotalTimeSlotDFeed).forEach((pm_code, index) => {
        const dayEpochs = pmsTotalTimeSlotDFeed[pm_code];
        const dispensedFeed = Object.keys(dayEpochs).map(dayEpoch => {
          const tdfeed = dayEpochs[dayEpoch].tdfeed;
          const tfeed = dayEpochs[dayEpoch].tfeed;
          // deleting the tdFeed and tffeed to iterate over the timeslots
          delete dayEpochs[dayEpoch].tdfeed;
          delete dayEpochs[dayEpoch].tfeed;
          const timeSlots = Object.values(dayEpochs[dayEpoch]);
          let htmlToolTipStr = `<table class="pm-sch-graph-toottip-table">
            <thead>
              <tr>
                <th scope="col" colspan="6" style="text-align:center;">
                 ${this.$moment(Number(dayEpoch)).format("DD MMM YYYY")}, ${
            (
              this.getPmIdPmDetailsOfSelectedPondId[
                mapCodeWithPmId[pm_code]
              ] || {
                title: pm_code
              }
            ).title
          }
                </th>
              </tr>
              <tr>
                <th scope="col" colspan="6" style="text-align:center;">
                ${this.$t("PM_tf")}:${Number(tfeed).toFixed(1)},${this.$t(
            "Comn_df"
          )}:${Number(tdfeed).toFixed(1)}
                </th>
              </tr>
              <tr>
              <th>${this.$t("PM_sch")}</th>
              <th>${this.$t("Comn_from")}</th>
              <th>${this.$t("Comn_to")}</th>
              <th>${this.$t("PM_tf")}</th>
              <th>${this.$t("Comn_df")}</th>
              </tr>
            </thead>
          `;
          timeSlots.forEach(({ timeslot, dfeed, feed }, index) => {
            const rowStr = `<tr>
            <th scope="row">${this.$t("s")}${index + 1}</td>
            <td>${timeslot.split("-")[0]}</td>
            <td>${timeslot.split("-")[1]}</td>
            <td>${Math.round(Number(feed))}</td>
            <td>${Math.round(Number(dfeed).toFixed(1))}</td>
            </tr>`;
            htmlToolTipStr += rowStr;
          });
          htmlToolTipStr += "</table>";
          return {
            x: Number(dayEpoch),
            y: tdfeed,
            tooltip_value: htmlToolTipStr
          };
        });
        dispensedFeed.sort((a, b) => a.x - b.x);
        series.push({
          name: (
            this.getPmIdPmDetailsOfSelectedPondId[mapCodeWithPmId[pm_code]] || {
              title: pm_code
            }
          ).title,
          data: dispensedFeed,
          // type: "areaspline",
          color: arrColors[index],
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, arrColors[index] + "ff"],
              [1, arrColors[index] + "33"]
            ]
          },
          tooltip: {
            pointFormat: "{point.tooltip_value}"
          },
          visible: false
        });
      });
      if (series.length > 0) {
        series[0].visible = true;
      } else {
        series = [];
      }
      return series;
    },
    createDatesBetweenToDates(fromDateInTimestamp, toDateInTimestamp) {
      const dates = [];
      for (let i = fromDateInTimestamp; i <= toDateInTimestamp; i += 86400000) {
        dates.push(i);
      }
      return dates;
    },
    getIndexOfDateBetweenToDates(
      fromDateInTimestamp,
      toDateInTimestamp,
      checkDate
    ) {
      return (checkDate - fromDateInTimestamp) / 86400000;
    },
    async handleValueChange(currentSelectedValues) {
      let from_date = this.$moment(currentSelectedValues[0]);
      let to_date = this.$moment(currentSelectedValues[1]);
      if (!this.checkDateWithInCultureRange(from_date)) {
        from_date = this.getMomentObjArrOfCultureDateRange[0];
      }
      if (!this.checkDateWithInCultureRange(to_date)) {
        to_date = this.getMomentObjArrOfCultureDateRange[1];
      }
      this.params.from_date = from_date.format("YYYY-MM-DD");
      this.params.to_date = to_date.format("YYYY-MM-DD");
      await this.initComponent(this.pondId);
    },
    initChart() {
      // if(this.feed_schedule_logs)
      const series = this.getPMScheduleFeedData(this.feed_schedule_logs);
      this.chartOptions.series = series;
    },
    getNColors(numOfColors) {
      const arrColors = [];
      const initL = (100 - 20) / numOfColors;
      const initH = (360 - 60) / numOfColors;
      for (let i = 1; i <= numOfColors; i++) {
        arrColors.push(this.hsl2rgb(initH * i, 96 * 0.01, initL * i * 0.01));
      }
      return arrColors;
    },
    hsl2rgb(h, s, l) {
      const a = s * Math.min(l, 1 - l);
      const f = (n, k = (n + h / 30) % 12) =>
        l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return this.rgbToHex(f(0), f(8), f(4));
    },
    rgbToHex(r, g, b) {
      return (
        "#" +
        [r, g, b]
          .map(x =>
            Math.round(x * 255)
              .toString(16)
              .padStart(2, 0)
          )
          .join("")
      );
    }
  }
};
</script>
<style lang="scss">
.pm-sch-graph-toottip-table {
  display: inline-block;
  max-height: 200px;
  box-sizing: border-box;
  overflow: auto;
  // overflow-x: hidden;
  td,
  th {
    border: 1px solid #ddd;
  }
}
</style>
